import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";

import { CustomPagination, Loading, CustomBreadCrumb } from "../../components";
import { RecipeType, PaginationType } from "../../DTO";
import { useAppNavigate, useAppPagination } from "../../Router/hook";
import { RecipeAPi } from "../../api";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";

export const RecipeList: FC = () => {
    const [pagination, setPagination] =
        useState<PaginationType>(useAppPagination());
    const [recipes, setRecipes] = useState<RecipeType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useAppNavigate();

    const user = useAppSelector(selectUser);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "Recipe List", href: "#" },
    ];

    const fetchRecipeList = (page: number) => {
        setLoading(true);

        RecipeAPi.ListRecipe(user, page)
            .then((data) => {
                setRecipes(data.recipe);
                setPagination(data.pagination);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                Swal.fire({
                    icon: "error",
                    text: err,
                });
            });
    };

    const handleActivateRecipe = (recipeId: number) => {
        RecipeAPi.ActivateRecipe(user, recipeId)
            .then(() => {
                Swal.fire("Acitvate!", "Recipe has been activate.", "success");
                fetchRecipeList(pagination.current_page);
            })
            .catch((err) =>
                Swal.fire({
                    icon: "error",
                    text: err,
                }),
            );
    };

    const handleDeactivateRecipe = (recipeId: number) => {
        RecipeAPi.DeactivateRecipe(user, recipeId)
            .then(() => {
                Swal.fire(
                    "Deacitvate!",
                    "Recipe has been deactivate.",
                    "success",
                );
                fetchRecipeList(pagination.current_page);
            })
            .catch((err) =>
                Swal.fire({
                    icon: "error",
                    text: err,
                }),
            );
    };

    useEffect(() => {
        fetchRecipeList(pagination.current_page);
    }, []);

    return (
        <>
            <Helmet>
                <title>D2Type | Recipe List</title>
                <meta name="description" content="Basic example" />
            </Helmet>
            <div className="d-flex flex-column">
                <CustomBreadCrumb breadCrumbList={crumbs} />
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="d-flex flex-column bg-custom-white rounded p-3">
                            <div className="d-flex flex-row flex-wrap justify-content-between align-items-center p-2 border-bottom">
                                <h4 className="text-nowrap">Recipe Table</h4>
                                <div className="d-flex flex-row gap-2">
                                    <Link
                                        className="text-white btn btn-success"
                                        to={"/recipe/new"}
                                    >
                                        Recipe Create+
                                    </Link>{" "}
                                </div>
                            </div>
                            <div className="p-4 text-center text-nowrap rounded p-3 shadow bg-custom-white h-100">
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            <th>No.</th>
                                            <th></th>
                                            <th>Title</th>
                                            <th>Created At</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="align-middle">
                                        {recipes.length > 0 &&
                                            recipes.map((item, index) => (
                                                <tr
                                                    key={item.id}
                                                    className="align-middle"
                                                >
                                                    <td>
                                                        {index +
                                                            1 +
                                                            (pagination.current_page -
                                                                1) *
                                                                pagination.per_page}
                                                    </td>
                                                    <td>
                                                        <img
                                                            src={
                                                                item.attachments
                                                                    .length > 0
                                                                    ? item
                                                                          .attachments[0]
                                                                          .path
                                                                    : ""
                                                            }
                                                            width={75}
                                                            height={75}
                                                            alt={
                                                                item.attachments
                                                                    .length > 0
                                                                    ? item
                                                                          .attachments[0]
                                                                          .attachableType
                                                                    : ""
                                                            }
                                                        />
                                                    </td>
                                                    <td>{item.title}</td>
                                                    <td>
                                                        {item?.createdAt.slice(
                                                            0,
                                                            10,
                                                        )}{" "}
                                                        {item?.createdAt.slice(
                                                            11,
                                                            19,
                                                        )}
                                                    </td>
                                                    <td>
                                                        <strong
                                                            className={`badge ${
                                                                item.status ===
                                                                "active"
                                                                    ? "bg-success"
                                                                    : "bg-danger"
                                                            }`}
                                                        >
                                                            {item.status}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-row gap-2 justify-content-center align-items-center align-self-center">
                                                            <Link
                                                                to={`/recipe/info/${item.id}`}
                                                                className="text-white btn btn-sm btn-info btn-sm"
                                                            >
                                                                Info
                                                            </Link>
                                                            <Link
                                                                to={`/recipe/edit/${item.id}`}
                                                                className="text-dark btn btn-warning btn-sm"
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faEdit
                                                                    }
                                                                />
                                                            </Link>
                                                            <Form.Switch
                                                                checked={
                                                                    item.status ===
                                                                    "active"
                                                                        ? true
                                                                        : false
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    if (
                                                                        e.target
                                                                            .checked
                                                                    ) {
                                                                        handleActivateRecipe(
                                                                            Number(
                                                                                item.id,
                                                                            ),
                                                                        );
                                                                    } else {
                                                                        handleDeactivateRecipe(
                                                                            Number(
                                                                                item.id,
                                                                            ),
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </Table>
                                <CustomPagination
                                    total={pagination.total}
                                    perPage={pagination.per_page}
                                    currentPage={pagination.current_page}
                                    changePage={(pageNum: number) => {
                                        navigate(
                                            `/recipe/list?current_page=${pageNum}`,
                                        );
                                        fetchRecipeList(pageNum);
                                    }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};
